import { useCallback, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { Storage } from '@aws-amplify/storage';

import { getAllImageAssets } from '@dyl/apiClients/backendApi';
import createDatabaseManager from '@dyl/utils/createDatabaseManager';
import createSnackbarAction from '@dyl/utils/createSnackbarAction';
import useAsync from '@dyl/hooks/useAsync';

/**
 * @typedef ImageAsset
 * @property {string} image_id
 * @property {string} created_at
 * @property {string} updated_at
 */

const { Provider: ImagesManager, use: useRaw } = createDatabaseManager({
   /** @returns {() => Promise<ImageAsset[]>} */
  requestHook: function useImagesRequest() {
    const { enqueueSnackbar } = useSnackbar();

    return useCallback(async (args = {}) => {
      const { image_id = '', page = 1, perPage = 10, orderBy = 'image_id', order = 'ASC' } = args;

      while (true) {
        try {
          return await getAllImageAssets({
            search: args.search || image_id || undefined,
            orderBy,
            order,
            page,
            perPage,
            version: '1'
          });
        } catch {
          await new Promise(resolve =>
            enqueueSnackbar('Failed to load images', {
              variant: 'error',
              persist: true,
              action: createSnackbarAction({
                iconComponent: RefreshIcon,
                onClick: resolve,
              }),
            })
          );
        }
      }
    }, [enqueueSnackbar]);
  },
  defaultValue: [],
  contextName: 'ImagesManager',
});

export default ImagesManager;

export function useImages(page, imageId = '', search = '', perPage = 10, sortModel = [{ field: 'image_id', sort: 'asc' }], auto = true) {
  const {
    data: images,
    status: imagesStatus,
    refresh: requestImages,
  } = useRaw();

  useEffect(() => {
    if (!auto) return;
    if (typeof page !== 'number') return;

    const params = {
      image_id: imageId || undefined,
      search: search || undefined,
      perPage,
      page: page + 1,
      orderBy: sortModel[0]?.field || 'image_id',
      order: (sortModel[0]?.sort || 'asc').toUpperCase()
    };
    requestImages(params);
  }, [auto, imageId, search, page, perPage, sortModel, requestImages]);

  const refreshImages = useCallback(() => {
    return requestImages({
      image_id: imageId || undefined,
      search: search || undefined,
      page: page + 1,
      perPage: perPage || 10,
      orderBy: sortModel[0]?.field || 'image_id',
      order: (sortModel[0]?.sort || 'asc').toUpperCase()
    });
  }, [requestImages, imageId, search, page, perPage, sortModel]);

  const imagesArray = Array.isArray(images)
    ? images
    : images?.images || [];

  const countFiltered = Array.isArray(images)
    ? images.length
    : images?.countFiltered || 0;

  return {
    images: imagesArray,
    countFiltered,
    imagesStatus,
    refreshImages,
  };
}

export function useImage(databaseId) {
    /** @type {string | null} */
    const src = useAsync(async () => {
        if (!databaseId) return null;

        try {
            return await Storage.get(`images/${databaseId}`, {
                level: 'public',
                customPrefix: {
                    public: '',
                },
            });
        }
        catch (error) {
            console.error(error);
            throw error;
        }
    }, [databaseId]);

    return { src };
}